import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { optionsFromPath, optionsOfAnswer, reportChartSeries, SERIES_TONES, TONES, } from "./manifest.js";
import { resolveLabel } from "./derive.js";
import { FieldControl, ManifestRefusal, fieldId, StateNotice, formatCell, formatDay, numbersIn, optionWords, useNarrowScreen } from "./render.js";
import { GroupedBars, TrendChart } from "./chart.js";
import { Button, InlineLink, Labeled, Menu, Notice, Select, TONE_CLASS, cx } from "./ui.js";
import { DateInput } from "./fields.js";
import { EmptyState, PageHead, Table } from "./blocks.js";
import { inspect } from "./manifest.js";
import { csvOf, download, exportFilename, tableOf, XLSX_TYPE } from "./export.js";
import { workbook } from "./export-xlsx.js";
import { wordDocument, DOCX_TYPE } from "./export-docx.js";
import { openPrintView, printDocument } from "./export-print.js";
import { choosesPaper } from "./paper.js";
import { PaperDialog } from "./list.js";
export const reportParams = (manifest) => manifest.report?.params ?? [];
/** The names a parameter is sent and kept under. */
export function paramNames(param) {
    return param.type === 'date_range' ? [`${param.key}_from`, `${param.key}_to`] : [param.key];
}
/** The required parameters still empty — a range needs both of its days. While any is, nothing is asked. */
export function missingParams(manifest, values) {
    return reportParams(manifest).filter((p) => p.required && paramNames(p).some((n) => !values[n]));
}
/** Whether the comparison is asked for: its parameter is a switch that is on, or a choice that is made. */
export function compareOn(manifest, values) {
    const compare = manifest.report?.compare;
    if (!compare)
        return false;
    const v = values[compare.param];
    return v !== undefined && v !== '' && v !== 'false';
}
const isMap = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
/**
 * The answer as a report, or null when it is not one. Rows are taken as they come, in their order;
 * a row with no words or no values is not a line anybody could read, and refuses the whole answer
 * rather than being dropped from it — a statement with a line silently missing is a wrong statement.
 * So does a cell's tone outside the six or a cell's words that are not words: the server decided
 * them, and a renderer that drew some other colour, or none, would be saying something it did not.
 * A column head that is not words is left out — the declared label is still true.
 */
export function reportAnswerOf(data) {
    if (!isMap(data))
        return null;
    const body = data;
    if (!Array.isArray(body.rows))
        return null;
    const rows = [];
    for (const r of body.rows) {
        if (!isMap(r))
            return null;
        const row = { ...r };
        const keyed = typeof row.label_key === 'string' && row.label_key !== '';
        if (row.label_key !== undefined && !keyed)
            return null;
        // A line said only by key still has words: the key itself, should the catalogue lack it.
        if (row.label === undefined && keyed)
            row.label = row.label_key;
        if (typeof row.label !== 'string' || !isMap(row.values))
            return null;
        if (row.params !== undefined && (!isMap(row.params) || Object.values(row.params).some((v) => typeof v !== 'string' && typeof v !== 'number')))
            delete row.params;
        if (row.tones !== undefined && (!isMap(row.tones) || Object.values(row.tones).some((t) => !TONES.includes(t))))
            return null;
        if (row.words !== undefined && (!isMap(row.words) || Object.values(row.words).some((w) => typeof w !== 'string')))
            return null;
        rows.push(row);
    }
    const notices = reportNoticesOf(body.notices);
    const heads = isMap(body.heads)
        ? Object.fromEntries(Object.entries(body.heads).filter((e) => typeof e[1] === 'string' && e[1].trim() !== ''))
        : {};
    return {
        rows,
        ...(Object.keys(heads).length ? { heads } : {}),
        ...(notices.length ? { notices } : {}),
        ...(typeof body.as_of === 'string' ? { as_of: body.as_of } : {}),
        ...(body.params && typeof body.params === 'object' && !Array.isArray(body.params) ? { params: body.params } : {}),
    };
}
const NOTICE_TONES = new Set(['green', 'amber', 'red', 'blue', 'violet', 'neutral']);
/**
 * The lines an answer says above its rows. One with no words is not a line anybody could read and is
 * left out; a tone outside the six is drawn neutral. Unlike a row, a notice never refuses the answer:
 * the rows are the statement, and a notice the renderer cannot read must not hide them.
 */
export function reportNoticesOf(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw.flatMap((n) => {
        if (!n || typeof n !== 'object' || Array.isArray(n))
            return [];
        const v = n;
        if (typeof v.text !== 'string' || !v.text.trim())
            return [];
        const tone = typeof v.tone === 'string' && NOTICE_TONES.has(v.tone) ? v.tone : 'neutral';
        const params = v.params && typeof v.params === 'object' && !Array.isArray(v.params) ? v.params : undefined;
        return [{ tone, text: v.text, ...(typeof v.key === 'string' && v.key ? { key: v.key } : {}), ...(params ? { params } : {}) }];
    });
}
/** A notice in the reader's words: the catalogue's under its key, else the server's sentence. */
export function noticeWords(notice, i18n) {
    if (!notice.key)
        return notice.text;
    const said = i18n.t(notice.key, notice.params);
    return said === notice.key ? notice.text : said;
}
/** A head the answer sent: a day in the reader's calendar, anything else as given. */
export function headWords(head, i18n) {
    return /^\d{4}-\d{2}-\d{2}$/.test(head) ? formatDay(head, numbersIn(i18n.locale)) : head;
}
/**
 * The columns in the order drawn: each declared one, then — with the comparison on — its compared
 * value and its difference. A head the answer sent for a column (`heads`) replaces its declared label.
 */
export function reportColumns(manifest, comparing, i18n, heads) {
    const compare = manifest.report?.compare;
    const out = [];
    const several = (manifest.columns ?? []).length > 1;
    for (const column of manifest.columns ?? []) {
        const head = heads?.[column.key];
        const label = head ? headWords(head, i18n) : resolveLabel(column.label, column.label_key, i18n);
        out.push({ key: column.key, label, column });
        if (!comparing || !compare)
            continue;
        const against = resolveLabel(compare.label, compare.label_key, i18n);
        const variance = compare.variance_label || compare.variance_label_key ? resolveLabel(compare.variance_label, compare.variance_label_key, i18n) : i18n.t('report.variance');
        // With one column the heads say only what they are; with several, whose they are as well.
        out.push({ key: `compare_${column.key}`, label: several ? `${label} · ${against}` : against, column: { ...column, key: `compare_${column.key}` } });
        out.push({ key: `variance_${column.key}`, label: several ? `${label} · ${variance}` : variance, column: { ...column, key: `variance_${column.key}` } });
    }
    return out;
}
const absent = (value) => value === null || value === undefined || value === '';
/** A line's words in the reader's language: the catalogue's under `label_key` (with `params`), else the server's `label`. */
export function rowLabel(row, i18n) {
    if (!row.label_key)
        return row.label;
    const said = i18n.t(row.label_key, row.params);
    return said === row.label_key ? row.label : said;
}
/** A value as its column draws it; no value is a dash, never a zero the server did not say. */
export function reportCell(value, column, i18n) {
    return absent(value) ? '—' : formatCell(value, column, i18n);
}
/**
 * A cell as the table draws it: the figure, the server's words for it after the figure (or alone,
 * where there is no figure), on the soft fill of the tone the server gave it. With neither words nor
 * a tone it is the plain figure. A tone is never colour alone: where it means something, the server
 * says so in `words`.
 */
export function reportCellNode(row, key, column, i18n) {
    const value = row.values?.[key];
    const tone = row.tones?.[key];
    const said = row.words?.[key]?.trim() ? row.words[key] : undefined;
    if (!tone && !said)
        return reportCell(value, column, i18n);
    const figure = absent(value) ? null : formatCell(value, column, i18n);
    return (_jsxs("span", { "data-report-cell": key, ...(tone ? { 'data-report-tone': tone } : {}), className: cx('inline-flex items-baseline gap-1.5', tone && cx('rounded-md px-1.5 py-0.5 font-semibold', TONE_CLASS[tone])), children: [figure ?? (said ? null : '—'), said ? _jsx("span", { dir: "auto", className: cx('text-[12px]', figure !== null && 'font-normal opacity-80'), "data-report-words": "", children: said }) : null] }));
}
/** Where a line opens: `module.screen`, or a screen of the report's own module, with its params. */
export function reportHref(ref, ownModule) {
    if (!ref || typeof ref.screen !== 'string' || !/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)?$/.test(ref.screen))
        return null;
    const [module, screen] = ref.screen.includes('.') ? ref.screen.split('.') : [ownModule, ref.screen];
    const pairs = Object.entries(ref.params ?? {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    return `#${module}/${screen}${pairs.length ? `?${pairs.join('&')}` : ''}`;
}
const DEPTH_INDENT = ['', 'ps-5', 'ps-10', 'ps-15', 'ps-20'];
// ── The chart ───────────────────────────────────────────────────────────────────────────────
/** A cell as a figure the chart can measure; anything else — absent, words, a switch — is a gap. */
const measured = (v) => {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean')
        return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
/**
 * The chart of a report (`report.chart`), from the rows as the server sent them and nothing else
 * (D-4): no sum, no share, no row of its own. Each line (a row neither `total` nor `subtotal`) is a
 * group named by its words or its `by` column; each series is a declared figure column, headed as the
 * table heads it (`heads` included), its value read as given and written by its column. The
 * comparison's columns are never series. Null when there is no chart, or nothing in it to measure —
 * no lines, or every value absent or zero — so a flat chart never reads as a measured nought.
 */
export function reportChart(manifest, answer, i18n) {
    const chart = manifest.report?.chart;
    const keys = reportChartSeries(manifest);
    if (!chart || !keys?.length)
        return null;
    const heads = reportColumns(manifest, false, i18n, answer.heads);
    const first = chart.tone ?? 'blue';
    const rest = SERIES_TONES.filter((t) => t !== first);
    const series = keys.flatMap((key, n) => {
        const head = heads.find((c) => c.key === key);
        return head ? [{ key, label: head.label, tone: n === 0 ? first : rest[(n - 1) % rest.length], column: head.column }] : [];
    });
    const byColumn = chart.by === 'label' ? undefined : (manifest.columns ?? []).find((c) => c.key === chart.by);
    const lines = answer.rows.filter((row) => row.kind !== 'total' && row.kind !== 'subtotal');
    const groups = lines.map((row, i) => {
        const name = byColumn ? reportCell(row.values?.[byColumn.key], byColumn, i18n) : rowLabel(row, i18n);
        return {
            key: String(i), label: name, title: name,
            values: series.map((s) => {
                const value = measured(row.values?.[s.key]);
                return { value, text: value === null ? '—' : reportCell(row.values?.[s.key], s.column, i18n) };
            }),
        };
    });
    const values = groups.flatMap((g) => g.values.map((v) => v.value)).filter((v) => v !== null);
    if (!values.length || values.every((v) => v === 0))
        return null;
    return { draw: chart.draw, series, groups };
}
/** The chart above the table: grouped bars, or a line per series along the rows. The table stays the statement. */
function ReportChartView({ parts, caption, i18n }) {
    const narrow = useNarrowScreen();
    if (parts.draw === 'bars')
        return _jsx(GroupedBars, { groups: parts.groups, series: parts.series, caption: caption, stacked: narrow });
    const first = parts.series[0].column;
    return (_jsx(TrendChart, { caption: caption, rtl: i18n.dir === 'rtl', width: narrow ? 340 : 880, series: parts.series.map((s, n) => ({
            key: s.key, label: s.label, tone: s.tone,
            points: parts.groups.map((g) => ({ key: g.key, label: g.title, title: g.title, value: g.values[n].value, text: g.values[n].text })),
        })), format: (v) => formatCell(v, { ...first, signed: false }, i18n) }));
}
/** `2026` a year, `2026-Q3` a quarter, `2026-09` a month; null for anything else. */
export function parsePeriod(value) {
    const m = /^(\d{4})(?:-Q([1-4])|-(0[1-9]|1[0-2]))?$/.exec(value ?? '');
    if (!m)
        return null;
    const year = Number(m[1]);
    if (m[2])
        return { length: 'quarter', year, part: Number(m[2]) };
    if (m[3])
        return { length: 'month', year, part: Number(m[3]) };
    return { length: 'year', year, part: 0 };
}
export function periodValue(p) {
    if (p.length === 'year')
        return String(p.year);
    if (p.length === 'quarter')
        return `${p.year}-Q${Math.min(4, Math.max(1, p.part))}`;
    return `${p.year}-${String(Math.min(12, Math.max(1, p.part))).padStart(2, '0')}`;
}
function monthName(month, locale) {
    return new Date(Date.UTC(2000, month - 1, 1)).toLocaleString(numbersIn(locale), { month: 'long', timeZone: 'UTC' });
}
/** A period in the reader's words: «سبتمبر 2026», «الربع 3 2026», «2026». */
export function periodWords(value, i18n) {
    const p = parsePeriod(value);
    if (!p)
        return value;
    if (p.length === 'year')
        return String(p.year);
    if (p.length === 'quarter')
        return `${i18n.t('report.period.q', { n: p.part })} ${p.year}`;
    return `${monthName(p.part, i18n.locale)} ${p.year}`;
}
/** What one parameter was set to, in words — for the line a file writes about itself. Null when it was not set. */
export function paramWords(param, values, i18n, options) {
    const locale = numbersIn(i18n.locale);
    const v = values[param.key];
    switch (param.type) {
        case 'date_range': {
            const from = values[`${param.key}_from`];
            const to = values[`${param.key}_to`];
            if (!from && !to)
                return null;
            return `${from ? formatDay(from, locale) : '…'} – ${to ? formatDay(to, locale) : '…'}`;
        }
        case 'date': return v ? formatDay(v, locale) : null;
        case 'period': return v ? periodWords(v, i18n) : null;
        case 'boolean': return v === undefined || v === '' ? null : i18n.t(v === 'true' ? 'value.yes' : 'value.no');
        default: {
            if (!v)
                return null;
            const option = (options ?? param.options ?? []).find((o) => String(o.value) === v);
            return option ? resolveLabel(option.label, option.label_key, i18n) : v;
        }
    }
}
// ── The file ────────────────────────────────────────────────────────────────────────────────
/**
 * The one table every format writes, from the rows as the server sent them: the line's words
 * (indented by its depth with spaces, so a spreadsheet keeps the shape), then each drawn column.
 * `tableOf` decides each cell exactly as the list's export does; it is handed no totals to add.
 */
export function reportTable(manifest, answer, comparing, ctx) {
    const columns = reportColumns(manifest, comparing, ctx.i18n, answer.heads);
    const flat = answer.rows.map((row) => ({
        __line: `${'    '.repeat(Math.max(0, Math.min(4, row.depth ?? 0)))}${rowLabel(row, ctx.i18n)}`,
        ...Object.fromEntries(columns.map((c) => [c.key, row.values?.[c.key]])),
    }));
    const table = tableOf(flat, [
        { key: '__line', label: ctx.i18n.t('report.line') },
        ...columns.map((c) => ({ ...c.column, label: c.label, label_key: undefined })),
    ], ctx);
    // The file writes the figure, never the tone; the server's words only where there is no figure.
    return {
        ...table,
        rows: table.rows.map((cells, r) => cells.map((cell, i) => {
            const { tone: _tone, ...plain } = cell;
            const key = i === 0 ? null : columns[i - 1]?.key;
            const said = key ? answer.rows[r]?.words?.[key] : undefined;
            return said?.trim() && absent(plain.raw) ? { text: said, raw: said } : plain;
        })),
    };
}
/**
 * `chosen`: the sheet picked in the paper dialog for this file (PDF and Word); the workbook keeps the
 * screen's declaration.
 */
export async function deliverReport(manifest, table, meta, format, ctx, chosen) {
    const paper = manifest.export?.paper;
    const printed = chosen ?? paper;
    if (format === 'xlsx')
        download(exportFilename(manifest, meta.at, 'xlsx'), await workbook(table, meta, ctx, { paper }), XLSX_TYPE);
    else if (format === 'docx')
        download(exportFilename(manifest, meta.at, 'docx'), await wordDocument(table, meta, ctx, { paper: printed }), DOCX_TYPE);
    else if (format === 'pdf')
        openPrintView(printDocument(table, meta, ctx, printed));
    else
        download(exportFilename(manifest, meta.at, 'csv'), csvOf(table));
}
// ── The parameter bar ───────────────────────────────────────────────────────────────────────
function PeriodControl({ id, param, value, onChange, i18n, now }) {
    const lengths = param.periods?.length ? param.periods : ['month', 'quarter', 'year'];
    const thisYear = now.getFullYear();
    const at = parsePeriod(value) ?? { length: lengths[0], year: thisYear, part: lengths[0] === 'quarter' ? Math.floor(now.getMonth() / 3) + 1 : now.getMonth() + 1 };
    const years = [...new Set([...Array.from({ length: 11 }, (_, i) => thisYear + 1 - i), at.year])].sort((a, b) => b - a);
    const set = (patch) => {
        const next = { ...at, ...patch };
        // A month kept while a quarter is chosen would name the wrong part: the part follows the length.
        if (patch.length && patch.length !== at.length)
            next.part = patch.length === 'quarter' ? Math.min(4, Math.floor((Math.max(1, at.part) - 1) / 3) + 1) : patch.length === 'month' ? (at.length === 'quarter' ? (at.part - 1) * 3 + 1 : 1) : 0;
        onChange(periodValue(next));
    };
    const parts = at.length === 'quarter' ? [1, 2, 3, 4].map((n) => ({ value: String(n), label: i18n.t('report.period.q', { n }) }))
        : at.length === 'month' ? Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: monthName(i + 1, i18n.locale) })) : [];
    return (_jsxs("span", { className: "flex flex-wrap items-center gap-2", "data-report-period": param.key, children: [lengths.length > 1 ? (_jsx(Select, { id: id, "aria-label": i18n.t('report.period.length'), value: at.length, onChange: (e) => set({ length: e.currentTarget.value }), options: lengths.map((l) => ({ value: l, label: i18n.t(`report.period.${l === 'year' ? 'whole' : l}`) })) })) : null, parts.length ? (_jsx(Select, { "aria-label": i18n.t('report.period.part'), value: String(at.part), onChange: (e) => set({ part: Number(e.currentTarget.value) }), options: parts })) : null, _jsx(Select, { ...(lengths.length > 1 ? {} : { id }), "aria-label": i18n.t('report.period.year'), value: String(at.year), onChange: (e) => set({ year: Number(e.currentTarget.value) }), options: years.map((y) => ({ value: String(y), label: String(y) })) })] }));
}
function ParamControl({ param, values, onChange, ctx, options, now }) {
    const label = resolveLabel(param.label, param.label_key, ctx.i18n);
    const hint = param.help || param.help_key ? resolveLabel(param.help, param.help_key, ctx.i18n) : undefined;
    return (
    // The label names the control by the id FieldControl gives it (fieldId): a label for `report-x` over
    // a control `field-report-x` named nothing, and a select or a switch was a button with no name (P08-03).
    _jsx(Labeled, { label: label, required: param.required ?? false, ...(hint ? { hint } : {}), id: fieldId(param.key, 'report'), children: ({ id }) => {
            if (param.type === 'date_range') {
                return (_jsxs("span", { className: "flex flex-wrap items-center gap-2", "data-report-range": param.key, children: [_jsx(DateInput, { id: id, locale: ctx.i18n.locale, "aria-label": `${label} · ${ctx.i18n.t('report.range.from')}`, value: values[`${param.key}_from`] ?? null, onChange: (v) => onChange(`${param.key}_from`, v ?? '') }), _jsx(DateInput, { locale: ctx.i18n.locale, "aria-label": `${label} · ${ctx.i18n.t('report.range.to')}`, value: values[`${param.key}_to`] ?? null, onChange: (v) => onChange(`${param.key}_to`, v ?? '') })] }));
            }
            if (param.type === 'period')
                return _jsx(PeriodControl, { id: id, param: param, value: values[param.key] ?? '', onChange: (v) => onChange(param.key, v), i18n: ctx.i18n, now: now });
            // A day, a choice, a switch: a form field's own control, so a report's select is the same lookup a form's is.
            const field = {
                key: param.key, label, type: param.type, ...(param.required ? { required: true } : {}),
                ...(param.type === 'select' ? { options: options ?? param.options ?? [], ...(param.options_from ? { options_from: param.options_from } : {}) } : {}),
            };
            const raw = values[param.key];
            const value = param.type === 'boolean' ? raw === 'true' : raw === undefined || raw === '' ? null : raw;
            return _jsx(FieldControl, { field: field, value: value, ctx: ctx, idPrefix: "report", onChange: (v) => onChange(param.key, v === null || v === undefined ? '' : String(v)) });
        } }));
}
/**
 * The report as drawn from what is already known: the head, the parameter bar, and the rows or the
 * state they are in. Pure, so every state is drawn the same in a test as on the page.
 */
export function ReportView({ manifest, ctx, draft, onDraft, onApply, applied, load, options = {}, exporter, now = new Date() }) {
    const { i18n } = ctx;
    const params = reportParams(manifest);
    const missing = missingParams(manifest, applied);
    const comparing = compareOn(manifest, applied);
    const ownModule = manifest.screen.split('.')[0] ?? '';
    const title = resolveLabel(manifest.title ?? manifest.screen, manifest.title_key, i18n);
    const lede = manifest.lede || manifest.lede_key ? resolveLabel(manifest.lede, manifest.lede_key, i18n) : undefined;
    const answer = load?.state === 'ok' ? load.data : null;
    const changed = paramsChanged(params, draft, applied);
    const formats = manifest.export && (!manifest.export.permission || ctx.holds(manifest.export.permission)) ? manifest.export.formats : [];
    // PDF and Word ask for the sheet first (PaperDialog); the file is built from what was asked then.
    const [asking, setAsking] = useState(null);
    const exportMenu = answer && answer.rows.length && formats.length ? (_jsx(Menu, { variant: "secondary", size: "md", trigger: i18n.t('list.export'), "data-report-export": "", items: formats.map((format) => ({
            key: format, label: i18n.t(`report.export.${format}`), attrs: { 'data-report-format': format },
            onPick: () => {
                const meta = {
                    title,
                    filters: params.map((p) => { const w = paramWords(p, applied, i18n, options[p.key]); return w ? `${resolveLabel(p.label, p.label_key, i18n)}: ${w}` : null; }).filter((x) => x !== null),
                    sort: '',
                    scope: answer.as_of ? i18n.t('report.asOf', { at: formatCell(answer.as_of, { key: 'as_of', label: '', type: answer.as_of.length > 10 ? 'datetime' : 'date' }, i18n) }) : '',
                    at: new Date().toISOString(),
                    ...(exporter ? { by: exporter } : {}),
                };
                const table = reportTable(manifest, answer, comparing, ctx);
                if (choosesPaper(format))
                    setAsking({ format, table, meta });
                else
                    void deliverReport(manifest, table, meta, format, ctx);
            },
        })) })) : null;
    const columns = reportColumns(manifest, comparing, i18n, answer?.heads);
    const chart = answer ? reportChart(manifest, answer, i18n) : null;
    const tableColumns = [
        { key: '__line', label: i18n.t('report.line') },
        ...columns.map((c) => ({ key: c.key, label: c.label, numeric: c.column.type !== 'text' })),
    ];
    const tableRows = (answer?.rows ?? []).map((row, i) => {
        const href = reportHref(row.ref, ownModule);
        const depth = Math.max(0, Math.min(DEPTH_INDENT.length - 1, Math.floor(Number(row.depth) || 0)));
        const words = _jsx("span", { dir: "auto", className: cx('block', DEPTH_INDENT[depth]), "data-report-depth": depth, children: href ? _jsx(InlineLink, { href: href, children: rowLabel(row, i18n) }) : rowLabel(row, i18n) });
        return {
            key: `${i}`,
            cells: { __line: words, ...Object.fromEntries(columns.map((c) => [c.key, reportCellNode(row, c.key, c.column, i18n)])) },
            ...(row.kind === 'total' ? { weight: 'total' } : row.kind === 'subtotal' ? { weight: 'strong' } : {}),
        };
    });
    return (_jsxs("div", { className: "grid min-w-0 gap-5", "data-screen": manifest.screen, "data-kind": "report", children: [_jsx(PageHead, { title: title, ...(lede ? { lede } : {}), actions: exportMenu }), formats.some(choosesPaper) ? (_jsx(PaperDialog, { open: asking !== null, format: asking?.format ?? 'pdf', declared: manifest.export?.paper, ctx: ctx, onPick: (paper) => { if (asking)
                    void deliverReport(manifest, asking.table, asking.meta, asking.format, ctx, paper); }, onClose: () => setAsking(null) })) : null, params.length ? (_jsxs("form", { "aria-label": i18n.t('report.params'), "data-report-params": "", className: "flex flex-wrap items-end gap-4 rounded-xl border bg-card p-4 shadow-card", onSubmit: (e) => { e.preventDefault(); onApply(); }, children: [params.map((p) => _jsx(ParamControl, { param: p, values: draft, onChange: onDraft, ctx: ctx, options: options[p.key], now: now }, p.key)), _jsx(Button, { type: "submit", variant: changed || !load ? 'primary' : 'secondary', "data-report-apply": "", children: i18n.t('report.apply') })] })) : null, missing.length ? (_jsx(EmptyState, { "data-report-missing": missing.map((p) => p.key).join(' '), title: i18n.t('report.missing.title', { names: missing.map((p) => resolveLabel(p.label, p.label_key, i18n)).join(i18n.t('value.separator')) }), description: i18n.t('report.missing.description') })) : !load || load.state === 'loading' ? (_jsx(StateNotice, { state: "loading", i18n: i18n })) : load.state === 'malformed' ? (_jsx(StateNotice, { state: "unavailable", i18n: i18n, detail: i18n.t('report.malformed') })) : load.state !== 'ok' ? (_jsx(StateNotice, { state: load.state, ...(load.detail ? { detail: load.detail } : {}), said: load, i18n: i18n })) : null, !missing.length && load?.state === 'ok' && load.data.notices?.length ? (_jsx("div", { className: "grid gap-2", "data-report-notices": "", children: load.data.notices.map((n, i) => _jsx(Notice, { tone: n.tone, "data-report-notice": n.key ?? '', children: noticeWords(n, i18n) }, i)) })) : null, missing.length || !load || load.state !== 'ok' ? null : !load.data.rows.length ? (_jsx(EmptyState, { "data-report-empty": "", title: i18n.t('report.empty') })) : (_jsxs("section", { className: "grid min-w-0 gap-2 rounded-xl border bg-card p-4 shadow-card", "data-report-rows": "", "data-report-compare": comparing ? 'on' : 'off', children: [load.data.as_of ? (_jsx("p", { className: "text-[13px] text-muted-foreground", "data-report-as-of": load.data.as_of, children: i18n.t('report.asOf', { at: formatCell(load.data.as_of, { key: 'as_of', label: '', type: load.data.as_of.length > 10 ? 'datetime' : 'date' }, i18n) }) })) : null, chart ? (_jsx("figure", { className: "m-0 min-w-0 border-b pb-4", "data-report-chart": chart.draw, children: _jsx(ReportChartView, { parts: chart, caption: i18n.t('report.chart.caption', { title }), i18n: i18n }) })) : null, _jsx(Table, { columns: tableColumns, rows: tableRows })] }))] }));
}
/** Whether the bar now says something other than what the rows were asked with. */
export function paramsChanged(params, draft, applied) {
    return params.some((p) => paramNames(p).some((n) => (draft[n] ?? '') !== (applied[n] ?? '')));
}
/**
 * Ask for the report with these values — or not at all: null, and `fetch` never called, while a
 * required parameter is empty. An answer that is not a report is said to be so, not drawn.
 */
export async function askReport(manifest, values, fetch) {
    if (missingParams(manifest, values).length)
        return null;
    const r = await fetch(values);
    if (r.state !== 'ok')
        return r;
    const answer = reportAnswerOf(r.data);
    return answer ? { state: 'ok', data: answer } : { state: 'malformed' };
}
/**
 * The report page: the manifest checked, the choices of each lookup read, and the rows asked for
 * what was applied — and only once every required parameter has a value. Applying again asks again.
 */
export function ReportScreen({ manifest, ctx, initial, fetch, read, onApplied, exporter }) {
    const problems = useMemo(() => inspect(manifest, (n) => ctx.components.has(n)).filter((p) => p.code !== 'UNKNOWN_COMPONENT'), [manifest, ctx.components]);
    const [draft, setDraft] = useState(initial);
    const [applied, setApplied] = useState(initial);
    const [round, setRound] = useState(0);
    const [load, setLoad] = useState(null);
    const [options, setOptions] = useState({});
    useEffect(() => {
        if (problems.length || missingParams(manifest, applied).length) {
            setLoad(null);
            return;
        }
        let live = true;
        setLoad({ state: 'loading' });
        void askReport(manifest, applied, fetch).then((r) => { if (live)
            setLoad(r); });
        return () => { live = false; };
    }, [applied, round, manifest, problems.length]);
    useEffect(() => {
        if (!read)
            return;
        let live = true;
        for (const p of reportParams(manifest)) {
            const from = p.type === 'select' ? p.options_from : undefined;
            const path = from ? optionsFromPath(from) : null;
            if (!from || !path)
                continue;
            void read(path).then((r) => {
                const found = r.state === 'ok' ? optionsOfAnswer(from, r.data, path, ctx.i18n.locale, optionWords(from, ctx.i18n)) : null;
                if (live && found)
                    setOptions((was) => ({ ...was, [p.key]: found }));
            });
        }
        return () => { live = false; };
    }, [manifest, read, ctx.i18n.locale]);
    if (manifest.permission && !ctx.holds(manifest.permission))
        return _jsx(StateNotice, { state: "forbidden", i18n: ctx.i18n, detail: manifest.permission });
    if (problems.length)
        return _jsx(ManifestRefusal, { problems: problems, i18n: ctx.i18n });
    return (_jsx(ReportView, { manifest: manifest, ctx: ctx, draft: draft, applied: applied, load: load, options: options, ...(exporter ? { exporter } : {}), onDraft: (name, value) => setDraft((was) => ({ ...was, [name]: value })), onApply: () => { setApplied({ ...draft }); setRound((n) => n + 1); onApplied?.({ ...draft }); } }));
}
//# sourceMappingURL=report.js.map